import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth, homeRoute } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner, Eye, EyeSlash } from "@phosphor-icons/react";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { useIndustries, IndustryBubbleBar, IndustryPanel, IndustryLogo } from "@/components/IndustryBubbles";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const industries = useIndustries();

  useEffect(() => {
    const saved = localStorage.getItem("aim_remember_email");
    if (saved) {
      setEmail(saved);
      setRemember(true);
    }
  }, []);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (remember) localStorage.setItem("aim_remember_email", email);
      else localStorage.removeItem("aim_remember_email");
      localStorage.removeItem("orbit_industry");
      login(data.access_token, data.user);
      navigate(params.get("redirect") || homeRoute(data.user?.role));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAFB]">
      <div className="hidden lg:block relative bg-white overflow-y-auto max-h-screen">
        <div className="p-12">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500 mb-2">Built for any inventory</p>
          <h2 className="font-display text-3xl font-extrabold text-zinc-900 leading-tight mb-6">Scan. Learn. Track. Move Forward.</h2>
          <p className="text-sm text-zinc-500 mb-3">Pick your industry to see the inventory options Orbit sets up for you.</p>
          <IndustryBubbleBar list={industries.list} selected={industries.selected} onSelect={industries.select} />
          <div className="mt-6">
            {industries.current ? <IndustryPanel industry={industries.current} /> : (
              <div className="space-y-6">
                {industries.list.map((u) => (
                  <button type="button" key={u.key} onClick={() => industries.select(u.key)} className="flex gap-4 text-left w-full rounded-xl p-2 -m-2 hover:bg-zinc-50 transition-colors" data-testid={`industry-row-${u.key}`}>
                    <IndustryLogo industryKey={u.key} label={u.label} className="h-14 w-14" />
                    <div>
                      <h3 className="font-display font-bold text-zinc-900">{u.label}</h3>
                      <p className="text-sm text-zinc-600 leading-relaxed mt-0.5">{u.desc}</p>
                      <p className="text-xs font-semibold text-zinc-400 mt-1.5">{u.tags}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-start justify-center p-6 pt-16 sm:pt-24 overflow-y-auto">
        <div className="w-full max-w-sm">
          <Link to="/" className="block mb-8">
            <img src="/orbit-logo.webp" alt="Orbit Inventory Management" className="w-full h-auto object-contain" />
          </Link>
          <h1 className="font-display text-3xl font-bold tracking-tight">Welcome back</h1>
          <p className="text-zinc-500 mt-1 mb-6">Sign in to your account to continue.</p>
          <div className="lg:hidden mb-6">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-500 mb-2">What do you track?</p>
            <IndustryBubbleBar list={industries.list} selected={industries.selected} onSelect={industries.select} compact />
            {industries.current && <div className="mt-3"><IndustryPanel industry={industries.current} /></div>}
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm font-semibold">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-12 mt-1.5 focus:ring-2 focus:ring-zinc-900" data-testid="login-email" placeholder="you@company.com" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
                <Link to="/forgot-password" className="text-sm text-zinc-900 font-medium underline underline-offset-2" data-testid="forgot-link">Forgot?</Link>
              </div>
              <div className="relative mt-1.5">
                <Input id="password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required className="h-12 pr-11 focus:ring-2 focus:ring-zinc-900" data-testid="login-password" placeholder="••••••••" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700" aria-label={showPw ? "Hide password" : "Show password"} data-testid="toggle-password">
                  {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2.5" data-testid="login-error">{error}</p>}
            <label className="flex items-center gap-2.5 cursor-pointer select-none py-1" data-testid="remember-me-label">
              <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} className="data-[state=checked]:bg-zinc-900 data-[state=checked]:border-zinc-900" data-testid="remember-me" />
              <span className="text-sm text-zinc-600">Save to this computer</span>
            </label>
            <Button type="submit" disabled={loading} className="w-full h-12 bg-zinc-900 hover:bg-zinc-800 text-white font-semibold" data-testid="login-submit">
              {loading ? <Spinner size={20} className="animate-spin" /> : "Sign In"}
            </Button>
          </form>
          <GoogleSignInButton label="Sign in with Google" />
          <p className="mt-6 text-sm text-zinc-500 text-center">Don't have an account? <Link to="/register" className="text-zinc-900 font-semibold underline underline-offset-2" data-testid="to-register">Start free</Link></p>
        </div>
      </div>
    </div>
  );
}
